import type { ChatUIMessage } from './types';

export type AssistantVisualState =
  | 'empty'
  | 'thinking'
  | 'activity'
  | 'content'
  | 'error';

type ChatPart = ChatUIMessage['parts'][number];
type ChatStatus = 'error' | 'ready' | 'streaming' | 'submitted';

const HIDDEN_TOOL_NAMES = new Set(['createDocument']);

function getToolName(part: ChatPart) {
  if (part.type === 'dynamic-tool') {
    return part.toolName ?? '';
  }

  return part.type.startsWith('tool-')
    ? part.type.replace(/^tool-/, '')
    : '';
}

function isToolPart(part: ChatPart) {
  return (part.type === 'dynamic-tool' || part.type.startsWith('tool-'))
    && 'state' in part;
}

function isVisibleDocumentPart(part: ChatPart) {
  return part.type === 'data-document-open'
    || part.type === 'data-document-preview'
    || part.type === 'data-document-finish';
}

function isVisibleContentPart(part: ChatPart, status: ChatStatus) {
  if (part.type === 'text' || part.type === 'reasoning') {
    return part.text.trim().length > 0;
  }

  if (part.type === 'file' || part.type === 'source-document' || part.type === 'source-url') {
    return true;
  }

  if (isVisibleDocumentPart(part) || part.type === 'data-rag-empty' || part.type === 'data-audio-transcription-batch') {
    return true;
  }

  if (part.type === 'data-rag-citations') {
    return status === 'ready' && part.data.citations.length > 0;
  }

  return false;
}

function isVisibleActivityPart(part: ChatPart) {
  if (part.type === 'data-web-research-activity' || part.type === 'data-document-generation-pending') {
    return true;
  }

  if (!isToolPart(part)) {
    return false;
  }

  return !HIDDEN_TOOL_NAMES.has(getToolName(part));
}

export function hasVisibleAssistantContent(message: ChatUIMessage, status: ChatStatus) {
  return message.parts.some((part) => isVisibleContentPart(part, status));
}

export function hasVisibleAssistantActivity(message: ChatUIMessage) {
  return message.parts.some(isVisibleActivityPart);
}

const ASK_USER_TOOL_NAME = 'ask_user';

function isAnsweredAskUserPart(part: ChatPart) {
  return isToolPart(part)
    && getToolName(part) === ASK_USER_TOOL_NAME
    && 'state' in part
    && part.state === 'output-available';
}

/**
 * Vrai quand le dernier message assistant porte un widget `ask_user` déjà répondu
 * et qu'aucune nouvelle sortie (texte, tool non-`ask_user`, artifact) n'est encore
 * visible : on attend que le modèle reprenne la main (continuation). Ce cas doit
 * afficher l'indicateur de réflexion — sinon l'écran reste muet entre la réponse
 * au widget et l'arrivée du contenu (ex. préparation d'une génération documentaire).
 */
export function isAwaitingAskUserContinuation(message: ChatUIMessage, status: ChatStatus) {
  if (message.role !== 'assistant') {
    return false;
  }

  let lastAnsweredIndex = -1;
  message.parts.forEach((part, index) => {
    if (isAnsweredAskUserPart(part)) {
      lastAnsweredIndex = index;
    }
  });

  if (lastAnsweredIndex < 0) {
    return false;
  }

  // Seul compte ce qui suit la dernière réponse : le texte qui introduisait la
  // question reste affiché avant la bulle de réponse et ne vaut pas continuation.
  const hasFreshOutput = message.parts.slice(lastAnsweredIndex + 1).some((part) => {
    if (isVisibleContentPart(part, status)) {
      return true;
    }
    return isVisibleActivityPart(part) && getToolName(part) !== ASK_USER_TOOL_NAME;
  });

  return !hasFreshOutput;
}

export function deriveAssistantVisualState(params: {
  hasTurnError?: boolean;
  message: ChatUIMessage;
  status: ChatStatus;
}): AssistantVisualState {
  if (params.hasTurnError) {
    return 'error';
  }

  if (params.message.role !== 'assistant') {
    return params.message.parts.length > 0 ? 'content' : 'empty';
  }

  if (hasVisibleAssistantContent(params.message, params.status)) {
    return 'content';
  }

  if (hasVisibleAssistantActivity(params.message)) {
    return 'activity';
  }

  if (params.status === 'submitted' || params.status === 'streaming') {
    return 'thinking';
  }

  return 'empty';
}

